/**
 * COPY STRATEGY — TaxTrapsPage
 *
 * Awareness:      Unaware (Stage 1/5)
 *                 Reader is a new driver who thinks Bolt payment = income.
 *                 Doesn't know about hidden taxes, commissions, or year-end debt.
 *
 * Sophistication: Stage 5 — Identification
 *                 Story-driven: "Tomas" case study. "80% naujų vairuotojų" —
 *                 reader sees themselves in the story. Identity: naive newcomer
 *                 vs informed professional.
 *
 * Avatar:         New driver, first 1–6 months. Excited about earnings.
 *                 Doesn't track expenses or understand net vs gross.
 *                 Searching "kiek uzdirba bolt vairuotojas" or similar.
 *
 * Hook:           Story shock — "Tomas manė, kad uždirba 1 125€. Realybė — 537€"
 * Structure:      Tomas story → What he sees vs reality → Hidden costs → Wake-up → CTA
 * Proof:          Line-by-line income breakdown showing real vs perceived earnings
 * Funnel:         Strongest top-of-funnel content, leads to DriverTaxesPage and signup
 */
import { motion } from 'framer-motion'
import { PiCheckCircle, PiWarning, PiEye } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const TaxTrapsPage = () => {
  usePageSetup('Nematomi mokesčių spąstai Bolt vairuotojams', {
    scroll: true,
    description: 'Tomas manė, kad uždirba €1 125. Realybė — €537. 80% naujų Bolt vairuotojų nežino apie paslėptus mokesčius ir pavasarį lieka skolingi VMI. Ar jūs vienas iš jų?',
    type: 'article',
    datePublished: '2026-03-05',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText as="h1" className="text-zinc-900 text-2xl font-medium mt-3 mb-2">
          Nematomi mokesčių spąstai: Kodėl 80% naujų Bolt vairuotojų pavasarį lieka skolingi VMI?
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Mokesčiai</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">8 min skaitymo</span>
        </MotionDiv>

        {/* Section 1: Tomas Case Study */}
        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Tomas pradėjo vairuoti Bolt prieš 6 mėnesius. Pirmą mėnesį Bolt programėlė rodė 1 500€ bruto pajamų. Į banko sąskaitą atėjo 1 125€.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Tomas buvo laimingas — pagaliau normalus uždarbis.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base font-medium">
          Bet Tomas nežinojo to, ką VMI jau žinojo apie jį.
        </MotionText>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Tomo mėnesinis „uždarbis"
        </MotionText>

        <MotionDiv className="p-4 bg-white border border-zinc-250 rounded-xl mb-4">
          <MotionText as="p" className="text-zinc-500 text-xs font-medium mb-3">
            KĄ TOMAS MATO
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3 mb-3">
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Bolt programėlėje rodo: 1 500 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Bolt komisiniai (25%): −375 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono border-t border-zinc-250 pt-2 mt-2 font-medium">
              Pervesta į banką: 1 125 €
            </MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs mt-1">
              ← Tomas mato TIK šį skaičių
            </MotionText>
          </MotionDiv>

          <MotionText as="p" className="text-red-600 text-xs font-medium mb-3">
            BET TOMAS DAR SKOLINGAS:
          </MotionText>
          <MotionDiv className="bg-red-50 p-3 rounded-lg">
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Atvirkštinis PVM (21% nuo 375€): −79 €
            </MotionText>
            <MotionText as="p" className="text-red-600 text-xs mb-2">
              ← IŠ SAVO KIŠENĖS
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Kuras (~25l/d × 1,65€ × 22d): −300 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              VSD (soc. draudimas): −84 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              PSD (sveikatos draudimas): −80 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              GPM (pajamų mokestis, 5% efektyvus): −45 €
            </MotionText>
            <MotionText as="p" className="text-red-700 text-sm font-mono border-t border-red-200 pt-2 mt-2 font-medium">
              TIKRASIS PELNAS: 537 €
            </MotionText>
            <MotionText as="p" className="text-red-600 text-xs mt-1 font-medium">
              Valandinis uždarbis (160 val): 3,36 €/val
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Tomas manė, kad uždirba 1 125€. Realybė — <strong>537€</strong>. Tai reiškia, kad Tomas dirba už <strong>3,36€ per valandą</strong>. Mažiau nei minimalus valandinis atlygis.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base">
          Bet svarbiausia — Tomas apie tai nesužinos iki <strong>gegužės 2 d.</strong>, kai turės pateikti GPM311 deklaraciją.
        </MotionText>

        {/* Section 2: DAC7 Threat */}
        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          DAC7: VMI jau žino
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Anksčiau vairuotojai galėjo „pamiršti" dalį pajamų. Nuo 2024 m. tai nebegalima.
        </MotionText>

        <MotionDiv className="p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiEye className="min-w-6 h-6 text-red-700" />
            <div>
              <MotionText as="h3" className="text-red-900 text-sm font-medium mb-2">
                DAC7 direktyva — VMI mato viską
              </MotionText>
              <MotionText as="p" className="text-red-800 text-sm mb-2">
                Bolt, Uber ir kitos platformos automatiškai praneša VMI apie VISAS jūsų pajamas:
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-red-800 text-sm">
                  Tikslią bruto sumą (su komisiniais)
                </li>
                <li className="text-red-800 text-sm">
                  Kiekvieną mėnesį atskirai
                </li>
                <li className="text-red-800 text-sm">
                  Jūsų asmens kodą ir IV numerį
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Bet VMI <strong>NEPASKAIČIUOJA</strong> jums Atvirkštinio PVM. Tai jūsų atsakomybė.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Jei Tomas deklaruoja 1 125€ pajamų, o VMI mato 1 500€ bruto — Tomas gauna pranešimą. Jei Tomas nedeklaruoja Atvirkštinio PVM — Tomas gauna baudą.
        </MotionText>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                Baudos
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-amber-800 text-sm">
                  Iki 30 d. vėlavimas: įspėjimas + delspinigiai
                </li>
                <li className="text-amber-800 text-sm">
                  Virš 30 d.: bauda 10–50% nuo nesumokėtos sumos
                </li>
                <li className="text-amber-800 text-sm">
                  Pakartotinis pažeidimas: sustiprintas tikrinimas
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        {/* Section 3: The Pivot — Hope */}
        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Bet Tomas neturi mesti vairavimo
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Problema ne ta, kad Bolt vairavimas neapsimoka. Problema ta, kad Tomas <strong>nežino savo tikrų skaičių</strong>. Jis nežino:
        </MotionText>

        <MotionDiv className="space-y-2 mb-4">
          <MotionDiv className="flex gap-2 items-start">
            <span className="text-zinc-300 text-sm mt-0.5">•</span>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Ar 30% išlaidų norma jam pigesnė, ar faktinės išlaidos?
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-2 items-start">
            <span className="text-zinc-300 text-sm mt-0.5">•</span>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Kiek tiksliai skolingas Atvirkštinio PVM kas mėnesį?
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex gap-2 items-start">
            <span className="text-zinc-300 text-sm mt-0.5">•</span>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Kada ir kiek mokėti VSD, PSD, GPM?
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base">
          Jei Tomas tai žinotų, jis galėtų optimizuoti savo maršrutus, pasirinkti teisingą atskaitymų metodą ir ramiai miegoti žinodamas, kad VMI neturi pretenzijų.
        </MotionText>

        {/* Section 4: The Solution — Finhudas */}
        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Sprendimas: žinoti savo tikrus skaičius
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-4">
          Jums nereikia būti buhalteriu. Jums reikia teisingo įrankio.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-4">
          <strong>Finhudas</strong> — tai apskaitos programa, sukurta specialiai Lietuvos pavežėjams. Štai kaip ji veikia:
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="p-5 space-y-5">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  1
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Įkelkite Bolt/Uber ataskaitą
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Parsisiųskite PDF iš partners.bolt.eu → įkelkite į Finhudas. Sistema automatiškai nuskaito pajamas, kelionių skaičių ir datas.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  2
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Komisinių PVM — automatiškai
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Įkelkite Bolt komisinių sąskaitą faktūrą. Finhudas apskaičiuoja Atvirkštinį PVM (21% nuo komisinių sumos) ir paruošia duomenis PVM 101 deklaracijai.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  3
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Mokesčių kalendorius
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Matote, kiek ir kada mokėti: PVM iki 25 d., PSD iki mėnesio pabaigos, GPM ir VSD — kasmet iki gegužės 2 d. Niekada nepraleidžiate terminų.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  4
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  30% vs faktinės — vienu paspaudimu
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Sistema palygina, kuris atskaitymų metodas taupo daugiau. Jei kuras + amortizacija + komisiniai viršija 30% — matote tikslią sutaupymą eurais.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  5
                </div>
              </div>
              <div>
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Mokesčiai pagal 2026 m. tarifus
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  GPM (20% su kredito sistema), VSD (12,52%), PSD (6,98%) — viskas apskaičiuota pagal naujausius Lietuvos tarifus.
                </MotionText>
              </div>
            </div>
          </div>

          {/* Tomas Redux */}
          <div className="mx-5 border-t border-zinc-250" />

          <div className="p-5">
            <div className="flex gap-3">
              <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <MotionText as="p" className="text-green-700 text-sm font-medium mb-1">
                  Jei Tomas naudotų Finhudas nuo pirmos dienos
                </MotionText>
                <ul className="space-y-1">
                  <li className="text-zinc-500 text-xs">
                    ✓ Žinotų, kad jo tikrasis pelnas yra ~537€, ne 1 125€
                  </li>
                  <li className="text-zinc-500 text-xs">
                    ✓ Kas mėnesį sumokėtų Atvirkštinį PVM laiku (79€)
                  </li>
                  <li className="text-zinc-500 text-xs">
                    ✓ Matytų, kad faktinės išlaidos jam pigesnės nei 30% norma
                  </li>
                  <li className="text-zinc-500 text-xs">
                    ✓ Gegužės 2 d. deklaracija būtų jau paruošta — be streso, be baudų
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </MotionDiv>

        <FinhudasCTA awareness="unaware" />

        <MotionText as="p" className="text-zinc-300 text-xs mb-4">
          *Straipsnyje pateikti skaičiai yra iliustraciniai ir pagrįsti 2026 m. mokesčių tarifais. Tikslios sumos priklauso nuo individualių metinių pajamų ir išlaidų. Finhudas neteikia mokesčių konsultacijų — tai įrankis, padedantis sekti ir skaičiuoti mokesčius.
        </MotionText>

        <RelatedArticles articles={[
          { slug: 'atvirkstinis-pvm-pavezejams', title: 'Atvirkštinis PVM: mokestis, kurį dauguma pamiršta' },
          { slug: 'dac7-atvirkstinis-pvm-kaip-isvengti-baudu', title: 'DAC7 ir Atvirkštinis PVM: kaip išvengti baudų' },
          { slug: 'individualios-veiklos-islaidos-30-procentu-arba-faktines', title: '30% norma ar faktinės išlaidos 2026' }
        ]} />

        <motion.div className="mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <ButtonLink variant="secondary" to="/blog">Grįžti į straipsnius</ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default TaxTrapsPage
